import React from "react";
import { toast } from "react-toastify";
import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import { bulkMoveCandidates, deactivateCandidates } from "model/CandidateModel";
import { moveCandidatesToAiRecruiterSchedule } from "model/AiRecruiterLogModel";

/**
 * Candidate table with optional left checkboxes and bulk actions:
 * - "Deactivate selected candidates" → Swal → POST `Candidate/deactivate` `{ ids: string[] }`
 * - "Bulk candidate transfer" → modal → POST `Candidate/bulk-move`
 *   `{ candidateClientIds: string[], targetClientId: string, jobId: string }`
 * - "Move to AI recruiter schedule" → Swal → POST `ai-recruiter/move-to-schedule` `{ ids: string[] }`
 * Pass `isCheckboxLeftSide={false}` and disable bulk flags for pages that are not
 * the main candidates list (e.g. candidate-hire-status).
 */
const CandidateOnboardingTableContainer = ({
  onDeactivateSuccess,
  onTransferSuccess,
  onMoveToAiRecruiterScheduleSuccess,
  isCheckboxLeftSide = true,
  enableBulkDeactivate = true,
  enableBulkTransfer = false,
  enableMoveToAiRecruiterSchedule = false,
  toggleSelectionOnRowClick,
  ...rest
}) => {
  const handleDeactivateCandidates = async (ids = []) => {
    if (!Array.isArray(ids) || !ids.length) return;
    const response = await deactivateCandidates({ ids });
    if (response?.success || response?.isSuccess || response?.status) {
      toast.success(response?.message || "Candidates deactivated successfully");
      onDeactivateSuccess?.(ids);
    } else {
      toast.error(response?.message || response?.error?.message || "Unable to deactivate candidates");
    }
  };

  const handleBulkTransferCandidates = async (payload = {}) => {
    const ids = (Array.isArray(payload?.ids) ? payload.ids : [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    const clientId = String(payload?.clientId ?? "").trim();
    const jobId = String(payload?.jobId ?? "").trim();
    if (!ids.length || !clientId || !jobId) return false;

    const response = await bulkMoveCandidates({
      candidateClientIds: ids,
      targetClientId: clientId,
      jobId,
    });
    if (response?.success || response?.isSuccess || response?.status) {
      toast.success(response?.message || "Candidates transferred successfully");
      onTransferSuccess?.({ ids, clientId, jobId, response });
      return true;
    }
    // toast.error(response?.message || response?.error?.message || "Unable to transfer candidates");
    return false;
  };

  const handleMoveToAiRecruiterSchedule = async (ids = []) => {
    const normalizedIds = (Array.isArray(ids) ? ids : [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    if (!normalizedIds.length) return false;

    const response = await moveCandidatesToAiRecruiterSchedule({ ids: normalizedIds });
    if (response?.success || response?.isSuccess || response?.status) {
      toast.success(response?.message || "Candidates moved to AI recruiter schedule");
      onMoveToAiRecruiterScheduleSuccess?.({ ids: normalizedIds, response });
      return true;
    }
    toast.error(
      response?.message || response?.error?.message || "Unable to move candidates to AI recruiter schedule"
    );
    return false;
  };

  const enableBulkSelection = Boolean(
    enableBulkDeactivate || enableBulkTransfer || enableMoveToAiRecruiterSchedule
  );
  const resolvedToggleSelectionOnRowClick =
    toggleSelectionOnRowClick ??
    Boolean(isCheckboxLeftSide && enableBulkSelection);

  return (
    <CommonTableForCandidate
      {...rest}
      isCheckboxLeftSide={isCheckboxLeftSide && enableBulkSelection}
      toggleSelectionOnRowClick={resolvedToggleSelectionOnRowClick}
      enableBulkDeactivate={enableBulkDeactivate}
      enableBulkTransfer={enableBulkTransfer}
      enableMoveToAiRecruiterSchedule={enableMoveToAiRecruiterSchedule}
      onDeactivateCandidates={
        enableBulkDeactivate && isCheckboxLeftSide ? handleDeactivateCandidates : undefined
      }
      onBulkTransferCandidates={
        enableBulkTransfer && isCheckboxLeftSide ? handleBulkTransferCandidates : undefined
      }
      onMoveToAiRecruiterSchedule={
        enableMoveToAiRecruiterSchedule && isCheckboxLeftSide
          ? handleMoveToAiRecruiterSchedule
          : undefined
      }
    />
  );
};

export default CandidateOnboardingTableContainer;
